import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { MapPin, Filter, RefreshCw, Loader2 } from "lucide-react";
import { MultiMarkerMap, renderPopupToHTML } from "../components/MultiMarkerMap";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";

const FREQ_COLORS = {
  Semanal: "#2563EB",
  Quinzenal: "#10B981",
  Mensal: "#F59E0B",
  Eventual: "#7C3AED",
  default: "#475569",
};

export default function ClientsMap() {
  const confirm = useConfirm();
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("Todos");
  const [recalculating, setRecalculating] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();

  async function reload() {
    const r = await api.get("/clients");
    setClients(r.data);
  }

  useEffect(() => {
    reload();
  }, []);

  async function recalcAll() {
    if (!(await confirm({
      title: "Recalcular GPS de todos os clientes?",
      description: "Isso vai usar o endereço atual de cada cliente (rua, cidade, estado, ZIP) para obter coordenadas pelo OpenStreetMap. Pode demorar alguns segundos.",
      confirmText: "Sim, recalcular",
    }))) return;
    setRecalculating(true);
    try {
      const { data } = await api.post("/clients/geocode-all");
      toast.success(
        `${data.updated_count} clientes atualizados${data.failed_count ? ` · ${data.failed_count} falharam` : ""}`,
      );
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRecalculating(false);
    }
  }

  const withGps = useMemo(
    () => clients.filter((c) => c.lat && c.lng),
    [clients],
  );
  const visible = useMemo(
    () =>
      filter === "Todos"
        ? withGps
        : withGps.filter((c) => c.frequency === filter),
    [withGps, filter],
  );
  const counts = useMemo(
    () =>
      withGps.reduce((acc, c) => {
        const f = c.frequency || "—";
        acc[f] = (acc[f] || 0) + 1;
        return acc;
      }, {}),
    [withGps],
  );

  const markers = useMemo(
    () =>
      visible.map((c) => ({
        id: c.id,
        lat: c.lat,
        lng: c.lng,
        color: FREQ_COLORS[c.frequency] || FREQ_COLORS.default,
        popupHTML: renderPopupToHTML(
          <div style={{ fontSize: 13, minWidth: 200 }} data-testid={`map-popup-${c.id}`}>
            <div style={{ fontWeight: 600, color: "#0F172A" }}>{c.name}</div>
            <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }}>
              {c.address}
              {c.apt_number ? ` · Apt ${c.apt_number}` : ""}
            </div>
            <div style={{ fontSize: 11, color: "#64748B" }}>
              {[c.city, c.state, c.zip_code].filter(Boolean).join(", ")}
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 9999,
                  color: "#fff",
                  background: FREQ_COLORS[c.frequency] || FREQ_COLORS.default,
                }}
              >
                {c.frequency || "—"}
              </span>
              <span style={{ fontSize: 10, color: "#64748B" }}>
                {c.property_type || ""}
              </span>
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
              <a
                href={`/clients?schedule=${c.id}`}
                style={{
                  flex: 1,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "6px 8px",
                  borderRadius: 8,
                  background: "#10B981",
                  color: "#fff",
                  textAlign: "center",
                  textDecoration: "none",
                }}
              >
                📅 Agendar
              </a>
              <a
                href={`/clients?focus=${c.id}`}
                style={{
                  flex: 1,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "6px 8px",
                  borderRadius: 8,
                  background: "#2563EB",
                  color: "#fff",
                  textAlign: "center",
                  textDecoration: "none",
                }}
              >
                Abrir cliente
              </a>
            </div>
          </div>,
        ),
      })),
    [visible],
  );

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            Operação
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <MapPin size={26} /> Mapa de clientes
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {visible.length} de {clients.length} clientes com GPS capturado
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {user?.role === "admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={recalcAll}
              disabled={recalculating}
              data-testid="recalc-all-gps-btn"
              title="Recalcular GPS de todos os clientes via OpenStreetMap"
            >
              {recalculating ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
              ) : (
                <RefreshCw size={14} className="mr-1" />
              )}
              {recalculating ? "Recalculando…" : "Recalcular GPS"}
            </Button>
          )}
          <Filter size={16} className="text-slate-400" />
          {["Todos", ...Object.keys(counts)].map((f) => {
            const active = filter === f;
            const color =
              f === "Todos" ? "#475569" : FREQ_COLORS[f] || FREQ_COLORS.default;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`map-filter-${f}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? "text-white border-transparent"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
                style={active ? { background: color } : {}}
              >
                {f}
                {f !== "Todos" && (
                  <span className="ml-1 opacity-80">({counts[f] || 0})</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="cm-card overflow-hidden"
        data-testid="clients-map-container"
      >
        <MultiMarkerMap markers={markers} height="calc(100vh - 240px)" />
      </div>

      {clients.length > 0 && withGps.length === 0 && (
        <div className="cm-card p-6 mt-4 text-center text-amber-700 bg-amber-50 border-amber-200">
          Nenhum cliente possui GPS capturado. Use o autocomplete de endereço ao
          cadastrar/editar clientes para capturar coordenadas automaticamente.
        </div>
      )}
    </AdminLayout>
  );
}
